import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { useQueryClient } from "@tanstack/react-query";
import { 
  mapStrategyListItemApiToUi, 
  mapStrategyDetailApiToUi,
  mapStrategyUiToCreateRequest,
  mapStrategyUiToUpdateRequest,
  getStrategyTypeId,
} from "@/lib/mappers/strategies";
import { generateMockStrategies } from "@/lib/mock-data/strategies-mock-data";
import type { CollectionStrategyItem } from "@/types/collection-strategies";
import type { 
  StrategiesApiResponse, 
  StrategyDetailApi,
  CreateStrategyResponse,
  UpdateStrategyResponse,
  StrategyTypeLookupApi,
  StrategyActionTypeLookupApi,
  TemplateListItemApi,
} from "@/types/api/strategies";
import type { StrategyAction } from "@/types/collection-strategies";

export interface GetStrategiesParams extends Record<string, string | number | boolean | undefined> {
  page?: number;
  pageSize?: number;
  search?: string;
  type?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

const mockStrategies = generateMockStrategies();

export function useStrategiesList(params?: GetStrategiesParams) {
  const { apiGet } = useApi();

  return useModeAwareQuery<CollectionStrategyItem[], Error>(
    ["strategies", params],
    async ({ signal }) => {
      const response = await apiGet<StrategiesApiResponse>("/api/v1/strategies", params, signal);
      return response.data.map(mapStrategyListItemApiToUi);
    },
    mockStrategies,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useStrategyById(strategyId: number | string | null) {
  const { apiGet } = useApi();
  const mockStrategy = mockStrategies.find(s => s.id === String(strategyId)) ?? null;

  return useModeAwareQuery<CollectionStrategyItem | null, Error>(
    ["strategy", strategyId],
    async ({ signal }) => {
      if (!strategyId) return null;
      const response = await apiGet<StrategyDetailApi>(`/api/v1/strategies/${strategyId}`, undefined, signal);
      return mapStrategyDetailApiToUi(response);
    },
    mockStrategy,
    {
      enabled: !!strategyId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

export function useCreateStrategy() {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<CreateStrategyResponse, Error, Partial<CollectionStrategyItem>>(
    async (data) => {
      const strategyTypeId = data.type ? getStrategyTypeId(data.type) : 1;
      const request = mapStrategyUiToCreateRequest(data, strategyTypeId);
      return apiPost<CreateStrategyResponse>("/api/v1/strategies", request);
    },
    async () => {
      return {
        id: Date.now(),
        name: "New Strategy",
        message: "Strategy created successfully",
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["strategies"] });
      },
    }
  );
}

export function useUpdateStrategy(strategyId: number | string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, Partial<CollectionStrategyItem>>(
    async (data) => {
      const strategyTypeId = data.type ? getStrategyTypeId(data.type) : undefined;
      const request = mapStrategyUiToUpdateRequest(data, strategyTypeId);
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${strategyId}`, request);
    },
    async () => {
      return {
        id: Number(strategyId),
        name: "Updated Strategy",
        message: "Strategy updated successfully",
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["strategies"] });
        queryClient.invalidateQueries({ queryKey: ["strategy", strategyId] });
      },
    }
  );
}

export function useDeleteStrategy() {
  const { apiDelete } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<{ message: string }, Error, number | string>(
    async (strategyId) => {
      return apiDelete<{ message: string }>(`/api/v1/strategies/${strategyId}`);
    },
    async () => {
      return { message: "Strategy deleted successfully" };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["strategies"] });
      },
    }
  );
}

export function useUpdateStrategyStatus(strategyId: number | string) {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, { status: "Active" | "Inactive" }>(
    async (data) => {
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${strategyId}`, data);
    },
    async () => {
      return {
        id: Number(strategyId),
        name: "Strategy",
        message: "Strategy status updated successfully",
      };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["strategies"] });
        queryClient.invalidateQueries({ queryKey: ["strategy", strategyId] });
      },
    }
  );
}

export function useToggleStrategyStatus() {
  const { apiPut } = useApi();
  const queryClient = useQueryClient();

  return useModeAwareMutation<UpdateStrategyResponse, Error, { id: number | string; status: "Active" | "Inactive" }>(
    async (data) => {
      return apiPut<UpdateStrategyResponse>(`/api/v1/strategies/${data.id}/status`, { status: data.status });
    },
    async (data) => {
      return {
        id: Number(data.id),
        name: "Strategy",
        message: "Strategy status updated successfully",
      };
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["strategies"] });
        queryClient.invalidateQueries({ queryKey: ["strategy", variables.id] });
      },
    }
  );
}

export interface StrategyTypeOption {
  id: number;
  code: string;
  name: string;
}

export interface ActionTypeOption {
  id: number;
  code: string;
  name: string;
}

const mockStrategyTypeOptions: StrategyTypeOption[] = [
  { id: 1, code: "standard", name: "Standard" },
  { id: 2, code: "intensive", name: "Intensive" },
  { id: 3, code: "vip", name: "VIP" },
  { id: 4, code: "high risk", name: "High Risk" },
];

const mockActionTypeOptions: ActionTypeOption[] = [
  { id: 1, code: "email", name: "Email" },
  { id: 2, code: "phone_call", name: "Phone Call" },
  { id: 5, code: "escalation", name: "Escalation" },
  { id: 6, code: "account_action", name: "Place On Stop" },
];

export function useStrategyTypeOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<StrategyTypeOption[], Error>(
    ["strategy-type-options"],
    async ({ signal }) => {
      const response = await apiGet<StrategyTypeLookupApi[]>("/api/v1/strategies/types", undefined, signal);
      return response.map((t) => ({ id: t.id, code: t.code, name: t.name }));
    },
    mockStrategyTypeOptions,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  );
}

export function useActionTypeOptions() {
  const { apiGet } = useApi();

  return useModeAwareQuery<ActionTypeOption[], Error>(
    ["action-type-options"],
    async ({ signal }) => {
      const response = await apiGet<StrategyActionTypeLookupApi[]>("/api/v1/strategies/action-types", undefined, signal);
      return response.map((t) => ({ id: t.id, code: t.code, name: t.name }));
    },
    mockActionTypeOptions,
    { staleTime: 60 * 1000, refetchOnWindowFocus: false }
  );
}

export interface TemplateOption {
  id: number;
  name: string;
}

const EMAIL_TEMPLATE_TYPE_ID = 1;

const mockEmailTemplateOptions: TemplateOption[] = [
  { id: 1, name: "Payment Reminder - Friendly" },
  { id: 2, name: "Payment Reminder - Urgent" },
  { id: 3, name: "Final Notice Before Collection" },
  { id: 4, name: "Payment Confirmation Request" },
];

export function useTemplatesByType(actionType: StrategyAction["type"] | undefined) {
  const { apiGet } = useApi();
  const isEmail = actionType === "email";

  return useModeAwareQuery<TemplateOption[], Error>(
    ["templates-by-type", isEmail ? EMAIL_TEMPLATE_TYPE_ID : null],
    async ({ signal }) => {
      if (!isEmail) return [];
      const response = await apiGet<TemplateListItemApi[]>(
        `/api/v1/templates/type/${EMAIL_TEMPLATE_TYPE_ID}`,
        undefined,
        signal
      );
      return response.map((t) => ({ id: t.id, name: t.name }));
    },
    isEmail ? mockEmailTemplateOptions : [],
    {
      enabled: isEmail,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}
